import React from 'react';
import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import GraystoneFederalLogo from '../components/GraystoneFederalLogo';

const MotionTypography = motion(Typography);

const Federal: React.FC = () => {
  const capabilities = [
    {
      title: 'Cyber Security',
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Protecting critical systems and data through proactive defense strategies.',
    },
    {
      title: 'Network, System & Cloud Infrastructure',
      icon: <StorageIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Designing and optimizing resilient, scalable IT environments.',
    },
    {
      title: 'Software & Product Development and Testing ',
      icon: <CodeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Building innovative, secure, and mission-ready applications.',
    },
    {
      title: 'Advanced Data Services',
      icon: <DataObjectIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      description: 'Leveraging analytics, machine learning, IoT, and AI to drive intelligent decision-making.',
    },
  ];

  const values = [
    {
      title: 'Transparency',
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Trust',
      icon: <HandshakeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Consistency',
      icon: <TimelineIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Predictability',
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
  ];

  return (
    <Box>
      {/* Federal Hero Component */}
      <Box
        sx={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          textAlign: 'center',
          pt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 4 },
          color: 'text.primary',
          overflow: 'hidden',
          overflowX: 'hidden',
          background: `linear-gradient(#0d0d0d, rgba(7, 7, 7, 0.6)), url('/images/american-flag.jpg') center/cover no-repeat`,
          minHeight: { xs: 200, sm: 240, md: 280 },
          mt: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justtifyContent: 'center', 
        }}
      >
        <Box
          sx={{ width: '40vw', minWidth: 300, maxWidth: 400, alignSelf: 'center',}}
          >
          <GraystoneFederalLogo />
        </Box>
        <MotionTypography 
          variant="h7" 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          sx={{ 
            fontSize: { xs: '1.0rem', sm: '1.25rem', md: '1.5rem' },
            // maxWidth: { xs: '100%', sm: '80%', md: '60%' },
            margin: '20px',
            lineHeight: 1.4,
            position: 'relative',
            zIndex: 2,
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 2px 8px rgba(0,0,0,0.25)',
            fontWeight: 300,
            fontStyle: 'italic',
            px: { xs: 2, sm: 4, md: 6, lg: 8, xl: 10 },
          }}
        >
            Mission-focused professional services firm driving technology implementation, modernization & securitization within the Public Sector
        </MotionTypography>
      </Box>


      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6 }}
          >
            Our Capabilities
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {capabilities.map((capability, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  {capability.icon}
                  <Typography variant="h6" sx={{ my: 2 }}>
                    {capability.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {capability.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 4 }}
          >
            About Us
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
              mb: 3,
              lineHeight: 1.8,
            }}
          >
            Graystone Federal is a mission-focused professional services firm committed to driving technology implementation, modernization, and securitization within the Public Sector. More specifically, we collaborate with prime contractors and government agencies to deliver innovative, mission-aligned solutions that enhance security, efficiency, and operational performance across the Federal Civilian, Defense, Intelligence, and SLED communities.
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
              mb: 3,
              lineHeight: 1.8,
            }}
          >
            In an era of evolving threats and rapid technological change, we provide the expertise necessary to safeguard national security, modernize critical infrastructure, and harness the power of advanced data analytics. From cybersecurity and cloud infrastructure to software development and AI-driven analytics, our solutions are designed to help federal agencies stay ahead of emerging challenges.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '900px',
              mx: 'auto',
              textAlign: 'center',
              lineHeight: 1.8,
            }}
          >
            At Graystone Federal, we believe in transparency, trust, consistency, and predictability—the foundation of every successful partnership. Our commitment to these values ensures that we deliver reliable, high-impact solutions & services that align with the United States Government’s mission.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6 }}
          >
            Core Values
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            {values.map((value, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  {value.icon}
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {value.title}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      
    </Box>
  );
};

export default Federal; 